/**
 * Министерства — the state's own buyers, which had no page of their own.
 *
 * A ministry is a Company node, so it used to render on the company page:
 * owners, tax debt, treasury payments, encumbrances. A ministry has none
 * of those in any meaningful sense, while the things it does have — a
 * portfolio, the cabinets that ran it, the agencies beneath it, who was
 * minister when a contract was signed — had nowhere on that page to go.
 *
 * The subordinate column is the reason the page is worth more than a
 * filter on the company list. Образование buys 611 mln directly and its
 * 69 subordinate bodies buy on top of that; reading the ministry alone
 * understates what the portfolio spends.
 */

import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { DataTable } from '../components/DataTable';
import {
  Amount, ErrorState, Link, Loading, PageHead, SectionHead,
} from '../components/ui';
import { num, truncate } from '../lib/format';

const MINISTRIES = graphql(`
  query Ministries {
    ministries {
      uic name portfolio
      tenders value units unitTenders unitValue
      ministers cabinets
    }
  }
`);

/* The portfolio key is a stem chosen to survive renaming, not a label to
   read. These are what a ministry is called. */
export const PORTFOLIO_LABELS: Record<string, string> = {
  'финанси': 'Финанси',
  'образование': 'Образование и наука',
  'здравеопазване': 'Здравеопазване',
  'вътрешни': 'Вътрешни работи',
  'отбрана': 'Отбрана',
  'транспорт': 'Транспорт и съобщения',
  'иновации': 'Иновации и растеж',
  'култура': 'Култура',
  'икономика': 'Икономика и индустрия',
  'труд': 'Труд и социална политика',
  'земеделие': 'Земеделие и храни',
  'туризъм': 'Туризъм',
  'регионално': 'Регионално развитие',
  'енергетика': 'Енергетика',
  'външни': 'Външни работи',
  'спорт': 'Младеж и спорт',
  'околна среда': 'Околна среда и води',
  'правосъдие': 'Правосъдие',
  'електронно': 'Електронно управление',
};

const label = (portfolio: string) => PORTFOLIO_LABELS[portfolio] ?? portfolio;

export function MinistriesPage() {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['ministries'],
    queryFn: () => request(MINISTRIES),
  });

  if (isPending) return <Loading rows={8} />;
  if (isError) return <ErrorState message={error.message} />;

  const ministries = data.ministries;

  return (
    <>
      <PageHead
        eyebrow="Държавата"
        title="Министерства"
        lede={'Министерствата като възложители: какво купуват те и какво купуват '
          + 'структурите под тях. Колоната „чрез структури“ е причината страницата '
          + 'да съществува — министерство с десетки агенции и дирекции харчи '
          + 'значително повече, отколкото личи от собствените му поръчки.'}
      />

      <section className="block">
        <SectionHead
          title="По портфейл"
          aside={`${num(ministries.length)} министерства · подредени по общо възложено`}
        />
        <DataTable
          rows={ministries}
          rowKey={(row) => row.uic}
          empty="Няма установени министерства."
          columns={[
            {
              key: 'portfolio',
              label: 'Портфейл',
              render: (row) => (
                <div>
                  <Link href={`#/ministries/${row.uic}`}>{label(row.portfolio)}</Link>
                  {/* The register name is often a string a procurement
                      notice used rather than the ministry's own title, so
                      it is shown small and second. */}
                  <div className="uic mono">{truncate(row.name ?? row.uic, 46)}</div>
                </div>
              ),
            },
            {
              key: 'value',
              label: 'Собствени поръчки',
              num: true,
              render: (row) => (
                <div>
                  <Amount value={row.value} />
                  <div className="uic mono">{`${num(row.tenders)} поръчки`}</div>
                </div>
              ),
            },
            {
              key: 'unitValue',
              label: 'Чрез структури',
              num: true,
              render: (row) => (row.units > 0
                ? (
                  <div>
                    <Amount value={row.unitValue} />
                    <div className="uic mono">
                      {`${num(row.unitTenders)} поръчки · ${num(row.units)} структури`}
                    </div>
                  </div>
                )
                // Not zero: no subordinate body of this ministry has been
                // identified, which is a gap in the hierarchy rather than
                // a ministry that has none.
                : <span className="muted">—</span>),
            },
            {
              key: 'ministers',
              label: 'Министри',
              num: true,
              render: (row) => (
                <div>
                  <span className="amount">{num(row.ministers)}</span>
                  <div className="uic mono">{`в ${num(row.cabinets)} кабинета`}</div>
                </div>
              ),
            },
          ]}
        />
      </section>

    </>
  );
}
